import * as Astronomy from "astronomy-engine";
import { add, type Vec3 } from "./coords";
import { assertSupportedDate } from "./time";
import { DWARF_IDS, dwarfHelioEcliptic, type DwarfId } from "./dwarf";
import { halleyHelioEcliptic } from "./comet";

export type PlanetId = "mercury" | "venus" | "earth" | "mars" | "jupiter" | "saturn" | "uranus" | "neptune";
export type BodyId = "sun" | PlanetId | "moon" | "pluto" | DwarfId | "halley";

export const PLANET_IDS: readonly PlanetId[] = ["mercury", "venus", "earth", "mars", "jupiter", "saturn", "uranus", "neptune"];
export const ALL_BODY_IDS: readonly BodyId[] = ["sun", ...PLANET_IDS, "moon", "pluto", ...DWARF_IDS, "halley"];

const ENGINE_BODY: Partial<Record<BodyId, Astronomy.Body>> = {
  mercury: Astronomy.Body.Mercury, venus: Astronomy.Body.Venus, earth: Astronomy.Body.Earth, mars: Astronomy.Body.Mars,
  jupiter: Astronomy.Body.Jupiter, saturn: Astronomy.Body.Saturn, uranus: Astronomy.Body.Uranus, neptune: Astronomy.Body.Neptune,
  pluto: Astronomy.Body.Pluto,
};

const EQJ_TO_ECL = Astronomy.Rotation_EQJ_ECL();
export function eqjToEcl(v: Astronomy.Vector): Vec3 {
  const r = Astronomy.RotateVector(EQJ_TO_ECL, v);
  return { x: r.x, y: r.y, z: r.z };
}

/** 月球相对地球的位置（黄道 J2000，AU，几何）。 */
export function moonGeocentric(date: Date): Vec3 {
  assertSupportedDate(date);
  return eqjToEcl(Astronomy.GeoMoon(Astronomy.MakeTime(date)));
}

/**
 * 日心位置（黄道 J2000，AU，几何，无光行差修正）。
 * 月球 = 地球日心位置 + 月球地心位置。
 */
export function heliocentricEcliptic(id: BodyId, date: Date): Vec3 {
  assertSupportedDate(date);
  if (id === "sun") return { x: 0, y: 0, z: 0 };
  if (id === "moon") return add(heliocentricEcliptic("earth", date), moonGeocentric(date));
  if (id === "halley") return halleyHelioEcliptic(date);
  if ((DWARF_IDS as readonly string[]).includes(id)) return dwarfHelioEcliptic(id as DwarfId, date);
  const body = ENGINE_BODY[id];
  if (!body) throw new Error(`Unknown body: ${id}`);
  return eqjToEcl(Astronomy.HelioVector(body, Astronomy.MakeTime(date)));
}

/** 一次取回所有天体（同一时刻）。 */
export function allHeliocentric(date: Date): Record<BodyId, Vec3> {
  const out = {} as Record<BodyId, Vec3>;
  for (const id of ALL_BODY_IDS) out[id] = heliocentricEcliptic(id, date);
  return out;
}
